import type { FunctionComponent } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { config } from '../../../../config/config';
import { useGetDocumentBySlugQuery } from '../../../documents/queries/documents.graphql';
import { ContextNavigate } from '../../../shared/components/context-navigate/context-navigate';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Loader } from '../../../shared/components/loader/loader';
import { selectLocale } from '../../../shared/store/locale.store';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { DocumentPage } from '../../components/document/document';

import styles from './documents.module.scss';

const style = createStyleHelper(styles, 'documents-container');

export interface IDocumentsProps {
    className?: string;
    slug: string;
    version?: string | null;
}

export const Documents: FunctionComponent<IDocumentsProps> = ({ className, slug, version }) => {
    const locale = useSelector(selectLocale);
    const intl = useIntl();

    const [{ data: documentList, fetching }] = useGetDocumentBySlugQuery({
        variables: {
            xTenantID: config.TENANT_ID,
            locale: locale,
            skinId: config.SKIN_ID,
            slug: slug,
            version: version || undefined,
        },
    });

    const documents = documentList?.getDocumentBySlug;

    if (fetching || !documentList) {
        return <Loader />;
    }

    if (!documents) {
        return <ContextNavigate to="/404" />;
    }

    if (!documents.content) {
        return <ContextNavigate to={`/documents/${slug}/`} />;
    }

    const lastUpdate = intl.formatDate(documents.lastUpdated || documents.dateCreated);

    return (
        <div className={createClassName([className, style()])}>
            <div className={style('version-info')}>
                <div className={style('version-text')}>
                    <FormattedMessage
                        id="documents.version-number"
                        defaultMessage="Version Number {version}"
                        values={{ version: documents.version }}
                    />
                </div>
                <div className={style('updated-text')}>
                    <FormattedMessage
                        id="documents.last-updated-on"
                        defaultMessage="Last Updated on {lastUpdated}"
                        values={{ lastUpdated: lastUpdate }}
                    />
                </div>
            </div>
            <DocumentPage content={documents.content ?? ''} />
        </div>
    );
};
